import { useNavigation } from '@/modules/common/hooks';
import { Box, Button, Grid, Paper, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TOP_BAR_HEIGHT_PX } from '@modules/common/components/TopBar';
import { DashboardTopBar } from '@common/components/DashboardTopBar';
import { ProjectServiceConnector } from '@/modules/api/ProjectApiConnector';
import { Project } from '@thive/fddm-types';
import { logger } from '@common/logging/logger';

export function ProjectsDashboard() {
  const { t } = useTranslation(['interface']);
  const [projects, setProjects] = useState<Project[]>([]);
  const { goToProject } = useNavigation();

  const getAllProjects = useCallback(async () => {
    try {
      const allProjects = await ProjectServiceConnector.getInstance().getProjects();
      setProjects(allProjects);
    } catch (error) {
      logger.error('failed to getAllProjects', error);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    getAllProjects();
  }, [getAllProjects]);

  return (
    <>
      <DashboardTopBar />

      <Box
        sx={{
          mt: TOP_BAR_HEIGHT_PX,
          overflowY: 'auto',
          height: '100vh', // ideally this wouldn't be needed because the parent has a height but i don't dare to change that...
          ml: '0',
          p: 4,
          textAlign: 'left',
          zIndex: 1,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {t('interface:home.your_projects', 'Your Projects')}
        </Box>

        <Grid sx={{ mt: '20px', mb: '40px' }} container spacing={2}>
          {projects.length > 0
            ? projects.map((project) => {
                const { name, ...rest } = project;

                return (
                  <Grid item key={`${project.lastUpdated}-${project.name}`} xs={12} md={6} lg={4}>
                    <Paper
                      sx={{
                        p: 2,
                      }}
                    >
                      <Typography variant='h5' component='h4' sx={{ mb: 2 }}>
                        {name}
                      </Typography>
                      {Object.entries(rest).map(([key, value]) => (
                        <Stack direction='row' key={`${key}-${value}`}>
                          <Typography
                            sx={{
                              width: '140px',
                              color: 'neutral.main',
                            }}
                          >
                            {key}:{' '}
                          </Typography>
                          <Typography
                            sx={{
                              flex: 1,
                              pb: 1,
                            }}
                          >
                            {key === 'description'
                              ? `${(value as string).substring(0, 90).trim()}...`
                              : value}
                          </Typography>
                        </Stack>
                      ))}
                      <Box sx={{ textAlign: 'right', mt: '20px' }}>
                        <Button
                          onClick={() => goToProject(project.id)}
                          data-testid={`project-${name}`}
                        >
                          Open
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })
            : t('interface:home.no_projects', 'There are no projects to display.')}
        </Grid>
      </Box>
    </>
  );
}
